import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { ProcessStepOption } from '@/api/resourceCapabilitiesApi'

/**
 * Choose the process steps a method covers.
 *
 * A multi-select, which this codebase has no primitive for — `SearchableSelect`
 * picks one thing. Built here rather than in `components/ui` because the shape it
 * needs is specific: steps are grouped by the form that authors them, since two
 * forms can name a step the same and the form is how you tell them apart.
 *
 * Selection is reported as the whole next list, so the caller stores it the way
 * the API takes it — a whole-list replace.
 */
export function ProcessStepPicker({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: ProcessStepOption[]
  /** Selected Process field ids. */
  value: string[]
  onChange: (fieldIds: string[]) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      o => o.label.toLowerCase().includes(q) || o.formName.toLowerCase().includes(q),
    )
  }, [options, query])

  /** Grouped by the form that authors the step — that is how they are told apart. */
  const groups = useMemo(() => {
    const byForm = new Map<string, ProcessStepOption[]>()
    for (const o of filtered) {
      const list = byForm.get(o.formName)
      if (list) list.push(o)
      else byForm.set(o.formName, [o])
    }
    return [...byForm.entries()]
  }, [filtered])

  const selected = new Set(value)

  function toggle(fieldId: string) {
    onChange(selected.has(fieldId) ? value.filter(id => id !== fieldId) : [...value, fieldId])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          disabled={disabled || options.length === 0}
          className='h-9 w-full justify-between font-normal'
        >
          <span className={cn(value.length === 0 && 'text-muted-foreground')}>
            {options.length === 0
              ? 'No process steps authored yet'
              : value.length === 0
                ? 'Select process steps…'
                : `${value.length} process ${value.length === 1 ? 'step' : 'steps'}`}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[22rem] p-0' align='start'>
        <div className='relative border-b p-2'>
          <Search className='absolute left-4 top-4 h-3.5 w-3.5 text-muted-foreground' />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search steps or forms…'
            className='h-8 pl-7 text-sm'
          />
        </div>
        <div className='max-h-72 overflow-auto p-1'>
          {groups.length === 0 ? (
            <p className='py-6 text-center text-sm text-muted-foreground'>No matching steps.</p>
          ) : (
            groups.map(([formName, steps]) => (
              <div key={formName} className='mb-1'>
                <p className='px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
                  {formName}
                </p>
                {steps.map(step => (
                  <label
                    key={step.fieldId}
                    className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent'
                  >
                    <Checkbox
                      checked={selected.has(step.fieldId)}
                      onCheckedChange={() => toggle(step.fieldId)}
                    />
                    <span className='flex-1'>{step.label}</span>
                    {selected.has(step.fieldId) && (
                      <Check className='h-3.5 w-3.5 text-primary' />
                    )}
                  </label>
                ))}
              </div>
            ))
          )}
        </div>
        {value.length > 0 && (
          <div className='border-t p-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-full text-xs'
              onClick={() => onChange([])}
            >
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
